import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { SphereMarker } from './SphereMarker';
import { Viewer } from '../viewer/viewer';
import { DefaultColors } from '../defines';
import type { Subscription, Coordinate } from '@pointorama/potree-shared';
import { getAnnotationColorProperty, getAnnotationVisibleProperty, getOutlineOuterRingProperty, getOutlineCavitiesProperty, getOutlineFloorHeightProperty } from '@pointorama/potree-shared';
import type { EditableShape, EditableShapeKind, EditEdge } from '../personas/tools/implementations/line-tool/EditableShape';

/**
 * Which ring of the outline is currently being edited. The LineTool edits
 * one closed ring at a time, so we route edit-mode calls through this state.
 *
 * `kind: 'outer'` targets the outer ring; `kind: 'hole'` targets `holes[index]`.
 */
export type OutlineRingSelector = { kind: 'outer' } | { kind: 'hole'; index: number };

/**
 * PointoramaOutline is a flat, floor-anchored polygon-with-holes that wraps
 * the union of the rooms on a single floor. The outer ring is the building's
 * external perimeter; the holes are interior cavities (courtyards, light
 * wells). All rings live at `floorHeight` on the world XY plane.
 *
 * One outline per floor; identified by `groupIdentifier` (the floor group id).
 */
export class PointoramaOutline extends THREE.Object3D implements EditableShape {
  private _identifier = '';
  groupIdentifier: string | null = null;
  selected = false;
  finished = false;
  depthFadeEnabled = true;
  boundingSphere?: THREE.Sphere;

  private outerRing: THREE.Vector3[] = [];
  private holes: THREE.Vector3[][] = [];
  private floorHeight: number;

  private color: THREE.Color;
  private viewer: Viewer;

  public outerSpheres: SphereMarker[] = [];
  public holeSpheres: SphereMarker[][] = [];

  /**
   * Flattened view of every sphere (outer + all holes) under the name the
   * renderers' `MeasureLike` iterator expects. Without this, the per-frame
   * depth-fade pass skips the outline's verts and they render in front of
   * point clouds and other annotations.
   */
  get spheres(): SphereMarker[] {
    const out: SphereMarker[] = [...this.outerSpheres];
    for (const ring of this.holeSpheres) out.push(...ring);
    return out;
  }
  private outerLineGeometry: LineGeometry;
  private outerLine: Line2;
  public lineMaterial: LineMaterial;
  public outerBackgroundLine: Line2;
  public backgroundLineMaterial: LineMaterial;
  private holeLines: { geometry: LineGeometry; line: Line2; backgroundLine: Line2 }[] = [];
  public holeLineMaterials: LineMaterial[] = [];

  private _isInEditMode = false;
  private _editingRing: OutlineRingSelector | null = null;
  private _editModeOriginalRing: THREE.Vector3[] | null = null;

  private _colorSubscription?: Subscription;
  private _visibleSubscription?: Subscription;
  private _outerRingSubscription?: Subscription;
  private _cavitiesSubscription?: Subscription;
  private _floorHeightSubscription?: Subscription;

  get identifier(): string {
    return this._identifier;
  }

  set identifier(value: string) {
    this._identifier = value;
    if (value) this.loadReactiveProperties();
  }

  constructor(viewer: Viewer, args: { outerRing?: THREE.Vector3[]; holes?: THREE.Vector3[][]; floorHeight?: number; color?: THREE.Color } = {}) {
    super();

    this.viewer = viewer;
    this.floorHeight = args.floorHeight ?? 0;
    this.color = args.color ?? DefaultColors.OBJECTS_THREE;
    this.outerRing = (args.outerRing ?? []).map((p) => p.clone());
    this.holes = (args.holes ?? []).map((h) => h.map((p) => p.clone()));

    this.outerLineGeometry = new LineGeometry();
    this.lineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 1,
      resolution: new THREE.Vector2(1000, 1000),
      depthTest: false,
      transparent: true,
    });
    this.outerLine = new Line2(this.outerLineGeometry, this.lineMaterial);
    this.add(this.outerLine);

    this.backgroundLineMaterial = new LineMaterial({
      color: this.color,
      linewidth: 15,
      resolution: new THREE.Vector2(1000, 1000),
      transparent: true,
      depthTest: false,
      opacity: 0.0,
    });
    this.outerBackgroundLine = new Line2(this.outerLineGeometry, this.backgroundLineMaterial);
    this.outerBackgroundLine.name = 'OutlineBackgroundLine';
    this.add(this.outerBackgroundLine);

    if (this.outerRing.length >= 3) {
      this.rebuildVisuals();
    }
  }

  /**
   * Subscribe to reactive properties (outer ring, cavities, floor height,
   * color, visibility) so the outline updates when the property store changes.
   * Mirrors the `PointoramaRoom.loadReactiveProperties` pattern.
   */
  private loadReactiveProperties(): void {
    if (!this._identifier) return;
    try {
      this._colorSubscription?.unsubscribe();
      this._visibleSubscription?.unsubscribe();
      this._outerRingSubscription?.unsubscribe();
      this._cavitiesSubscription?.unsubscribe();
      this._floorHeightSubscription?.unsubscribe();

      const outerRingProp = getOutlineOuterRingProperty(this._identifier);
      const cavitiesProp = getOutlineCavitiesProperty(this._identifier);
      const floorHeightProp = getOutlineFloorHeightProperty(this._identifier);

      // Initial load: only read from properties if the constructor didn't
      // already supply geometry. (createAnnotation seeds properties before
      // the scene object is built, so the property values are authoritative
      // after the very first construction.)
      if (this.outerRing.length === 0) {
        const coords = outerRingProp.value;
        if (coords.length >= 3) this.outerRing = coords.map((c: Coordinate) => new THREE.Vector3(c.x, c.y, c.z));
      }
      if (this.holes.length === 0) {
        const cavityCoords = cavitiesProp.value;
        if (Array.isArray(cavityCoords)) {
          this.holes = cavityCoords.map((ring: Coordinate[]) => ring.map((c) => new THREE.Vector3(c.x, c.y, c.z)));
        }
      }
      this.floorHeight = floorHeightProp.value;

      if (this.outerRing.length >= 3) this.rebuildVisuals();

      this._outerRingSubscription = outerRingProp.subscribeToChanges((coords: Coordinate[]) => {
        if (coords.length >= 3) {
          this.outerRing = coords.map((c) => new THREE.Vector3(c.x, c.y, c.z));
          this.rebuildVisuals();
        }
      });
      this._cavitiesSubscription = cavitiesProp.subscribeToChanges((cavityCoords: Coordinate[][]) => {
        if (Array.isArray(cavityCoords)) {
          this.holes = cavityCoords.map((ring) => ring.map((c) => new THREE.Vector3(c.x, c.y, c.z)));
          this.rebuildVisuals();
        }
      });
      this._floorHeightSubscription = floorHeightProp.subscribeToChanges((h: number) => {
        this.floorHeight = h;
      });

      const colorProperty = getAnnotationColorProperty(this._identifier);
      this.setColor(new THREE.Color(colorProperty.value));
      this._colorSubscription = colorProperty.subscribeToChanges((value: string) => {
        this.setColor(new THREE.Color(value));
      });

      const visibleProperty = getAnnotationVisibleProperty(this._identifier);
      this.visible = visibleProperty.value;
      this._visibleSubscription = visibleProperty.subscribeToChanges((value: boolean) => {
        this.visible = value;
      });
    } catch {
      // Global property service not initialized yet.
    }
  }

  getFloorHeight(): number {
    return this.floorHeight;
  }

  getOuterRing(): THREE.Vector3[] {
    return this.outerRing;
  }

  getHoles(): THREE.Vector3[][] {
    return this.holes;
  }

  setRings(outerRing: THREE.Vector3[], holes: THREE.Vector3[][]): void {
    this.outerRing = outerRing.map((p) => p.clone());
    this.holes = holes.map((h) => h.map((p) => p.clone()));
    this.rebuildVisuals();
  }

  /**
   * All edges of every ring as flat segments — used by the constraint snap
   * module as reference geometry while editing a different ring.
   */
  getAllEdges(): { start: THREE.Vector3; end: THREE.Vector3 }[] {
    const edges: { start: THREE.Vector3; end: THREE.Vector3 }[] = [];
    const addRing = (ring: THREE.Vector3[]): void => {
      const n = ring.length;
      for (let i = 0; i < n; i++) edges.push({ start: ring[i].clone(), end: ring[(i + 1) % n].clone() });
    };
    addRing(this.outerRing);
    for (const h of this.holes) addRing(h);
    return edges;
  }

  // ─── EditableShape ──────────────────────────────────────────────────

  getKind(): EditableShapeKind {
    return 'outline';
  }

  /**
   * Choose which ring the next `enterEditMode` will operate on. Called by
   * OutlineKindAdapter once it has hit-tested which ring the user clicked.
   * Defaults to the outer ring if never called.
   */
  setEditingRing(selector: OutlineRingSelector): void {
    this._editingRing = selector;
  }

  enterEditMode(): EditEdge[] {
    const selector = this._editingRing ?? { kind: 'outer' as const };
    const ring = selector.kind === 'outer' ? this.outerRing : this.holes[selector.index];
    if (!ring || ring.length < 3) return [];

    this._editModeOriginalRing = ring.map((p) => p.clone());
    this._isInEditMode = true;

    this.outerLine.visible = false;
    this.outerBackgroundLine.visible = false;
    this.outerSpheres.forEach((s) => (s.visible = false));
    this.holeLines.forEach(({ line, backgroundLine }) => {
      line.visible = false;
      backgroundLine.visible = false;
    });
    this.holeSpheres.forEach((arr) => arr.forEach((s) => (s.visible = false)));

    const edges: EditEdge[] = [];
    const n = ring.length;
    for (let i = 0; i < n; i++) {
      const start = ring[i];
      const end = ring[(i + 1) % n];
      edges.push({
        start: start.clone(),
        end: end.clone(),
        midpoint: new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5),
        direction: new THREE.Vector3().subVectors(end, start).normalize(),
      });
    }
    return edges;
  }

  exitEditMode(confirm: boolean, newPolygon?: THREE.Vector3[]): void {
    if (!this._isInEditMode) return;

    const selector = this._editingRing ?? { kind: 'outer' as const };

    if (confirm && newPolygon && newPolygon.length >= 3) {
      const ring = newPolygon.map((p) => new THREE.Vector3(p.x, p.y, this.floorHeight));
      if (selector.kind === 'outer') this.outerRing = ring;
      else this.holes[selector.index] = ring;
    } else if (!confirm && this._editModeOriginalRing) {
      if (selector.kind === 'outer') this.outerRing = this._editModeOriginalRing.map((p) => p.clone());
      else this.holes[selector.index] = this._editModeOriginalRing.map((p) => p.clone());
    }

    this._isInEditMode = false;
    this._editModeOriginalRing = null;
    this._editingRing = null;
    this.rebuildVisuals();
  }

  getBasePlane(): { position: THREE.Vector3 } {
    return { position: new THREE.Vector3(0, 0, this.floorHeight) };
  }

  get isInEditMode(): boolean {
    return this._isInEditMode;
  }

  // ─── Visuals ────────────────────────────────────────────────────────

  private rebuildVisuals(): void {
    this.disposeSpheres();
    this.disposeHoleLines();

    if (this.outerRing.length >= 3) this.position.copy(this.outerRing[0]);

    const outerVerts: number[] = [];
    for (const p of this.outerRing) {
      outerVerts.push(p.x - this.position.x, p.y - this.position.y, p.z - this.position.z);
      const sphere = new SphereMarker(this.viewer);
      sphere.setColor(this.color);
      sphere.setScaleFactor(this.viewer.scene.outlineSphereScale);
      sphere.position.set(p.x - this.position.x, p.y - this.position.y, p.z - this.position.z);
      this.outerSpheres.push(sphere);
      this.add(sphere);
    }
    if (this.outerRing.length > 0) {
      const first = this.outerRing[0];
      outerVerts.push(first.x - this.position.x, first.y - this.position.y, first.z - this.position.z);
    }
    // Dispose + re-setPositions rebuilds the internal attribute buffer.
    // Same pattern PointoramaRoom uses to guarantee Line2 picks up the new geometry.
    this.outerLineGeometry.dispose();
    if (outerVerts.length >= 6) {
      this.outerLineGeometry.setPositions(outerVerts);
      this.outerLine.computeLineDistances();
      this.outerBackgroundLine.computeLineDistances();
      this.lineMaterial.needsUpdate = true;
      this.backgroundLineMaterial.needsUpdate = true;
    }
    // Restore visibility — `enterEditMode` hides these and `rebuildVisuals`
    // is the natural place to bring them back since it's called from `exitEditMode`.
    this.outerLine.visible = true;
    this.outerBackgroundLine.visible = true;

    for (let h = 0; h < this.holes.length; h++) {
      const hole = this.holes[h];
      const geometry = new LineGeometry();
      const verts: number[] = [];
      const spheres: SphereMarker[] = [];
      for (const p of hole) {
        verts.push(p.x - this.position.x, p.y - this.position.y, p.z - this.position.z);
        const sphere = new SphereMarker(this.viewer);
        sphere.setColor(this.color);
        sphere.setScaleFactor(this.viewer.scene.outlineSphereScale);
        sphere.position.set(p.x - this.position.x, p.y - this.position.y, p.z - this.position.z);
        spheres.push(sphere);
        this.add(sphere);
      }
      if (hole.length > 0) {
        const first = hole[0];
        verts.push(first.x - this.position.x, first.y - this.position.y, first.z - this.position.z);
      }
      if (verts.length >= 6) geometry.setPositions(verts);
      const lineMat = new LineMaterial({ color: this.color, linewidth: 1, resolution: new THREE.Vector2(1000, 1000), depthTest: false, transparent: true });
      const bgMat = new LineMaterial({ color: this.color, linewidth: 15, resolution: new THREE.Vector2(1000, 1000), transparent: true, depthTest: false, opacity: 0.0 });
      const line = new Line2(geometry, lineMat);
      const backgroundLine = new Line2(geometry, bgMat);
      backgroundLine.name = 'OutlineHoleBackgroundLine';
      line.computeLineDistances();
      backgroundLine.computeLineDistances();
      lineMat.needsUpdate = true;
      bgMat.needsUpdate = true;
      this.add(line);
      this.add(backgroundLine);
      this.holeLineMaterials.push(lineMat, bgMat);
      this.holeLines.push({ geometry, line, backgroundLine });
      this.holeSpheres.push(spheres);
    }

    this.updateBoundingSphere();
  }

  private disposeSpheres(): void {
    this.outerSpheres.forEach((s) => this.remove(s));
    this.outerSpheres = [];
    this.holeSpheres.forEach((arr) => arr.forEach((s) => this.remove(s)));
    this.holeSpheres = [];
  }

  private disposeHoleLines(): void {
    for (const h of this.holeLines) {
      this.remove(h.line);
      this.remove(h.backgroundLine);
      h.geometry.dispose();
    }
    this.holeLines = [];
    this.holeLineMaterials.forEach((m) => m.dispose());
    this.holeLineMaterials = [];
  }

  private updateBoundingSphere(): void {
    if (this.outerRing.length === 0) {
      this.boundingSphere = undefined;
      return;
    }
    const origin = this.position;
    const pts = this.outerRing.map((p) => p.clone().sub(origin));
    for (const h of this.holes) for (const p of h) pts.push(p.clone().sub(origin));
    this.boundingSphere = new THREE.Box3().setFromPoints(pts).getBoundingSphere(new THREE.Sphere());
  }

  /** Called by the viewer's render loop; matches the PointoramaRoom contract. */
  update(): void {
    const size = this.viewer.renderer.getSize(new THREE.Vector2());
    this.lineMaterial.resolution.set(size.width, size.height);
    this.backgroundLineMaterial.resolution.set(size.width, size.height);
    for (const m of this.holeLineMaterials) m.resolution.set(size.width, size.height);
  }

  setColor(color: THREE.Color): void {
    this.color = color;
    this.lineMaterial.color = color;
    this.backgroundLineMaterial.color = color;
    for (const m of this.holeLineMaterials) m.color = color;
    this.outerSpheres.forEach((s) => s.setColor(color));
    this.holeSpheres.forEach((arr) => arr.forEach((s) => s.setColor(color)));
  }

  dispose(): void {
    this._colorSubscription?.unsubscribe();
    this._visibleSubscription?.unsubscribe();
    this._outerRingSubscription?.unsubscribe();
    this._cavitiesSubscription?.unsubscribe();
    this._floorHeightSubscription?.unsubscribe();

    this.disposeSpheres();
    this.disposeHoleLines();
    this.outerLineGeometry.dispose();
    this.lineMaterial.dispose();
    this.backgroundLineMaterial.dispose();
    this.remove(this.outerLine);
    this.remove(this.outerBackgroundLine);
  }
}
